import { computed, onMounted, onUnmounted, ref, type ComputedRef, type Ref } from "vue";
import { getKeyboardSafeMenuHeight, type KeyboardSafeMenuDirection } from "@/helpers/landscapeKeyboard";
import DeviceUtility from "@/helpers/device";

type KeyboardSafeMenuOptions = {
	direction?: KeyboardSafeMenuDirection;
};

type KeyboardSafeMenuProps = {
	// 0 overrides the global vuetify.js default (-250 on mobile, -150 on tablet)
	// which allowed menus to overlap the on-screen keyboard. maxWidth preserves
	// the global "100%" constraint that per-component menuProps would otherwise drop.
	viewportMargin: 0;
	maxWidth: "100%";
	maxHeight: string;
};

export function useKeyboardSafeMenu(
	activatorRef?: Ref<HTMLElement | null>,
	options: KeyboardSafeMenuOptions = {}
): {
	menuProps: ComputedRef<Partial<KeyboardSafeMenuProps>>;
} {
	const resizeVersion = ref(0);
	let isMounted = false;
	let visualViewportTarget: VisualViewport | null = null;
	const handleResize = () => {
		if (!isMounted) {
			return;
		}
		resizeVersion.value += 1;
	};

	const menuProps = computed<Partial<KeyboardSafeMenuProps>>(() => {
		if (!DeviceUtility.isMobile && !DeviceUtility.isTablet) {
			// Per-component menuProps replace Vuetify globals wholesale; preserve the
			// app-wide maxWidth constraint that vuetify.js defines for all dropdowns.
			return { maxWidth: "100%" };
		}

		resizeVersion.value; // reactive dependency — re-evaluates on viewport/keyboard resize

		const activatorElement = activatorRef?.value;
		const activatorRect =
			activatorElement && typeof activatorElement.getBoundingClientRect === "function"
				? activatorElement.getBoundingClientRect()
				: null;
		return {
			viewportMargin: 0,
			maxWidth: "100%",
			maxHeight: `${getKeyboardSafeMenuHeight(activatorRect, { direction: options.direction })}px`
		};
	});

	onMounted(() => {
		isMounted = true;
		visualViewportTarget = window.visualViewport ?? null;
		visualViewportTarget?.addEventListener("resize", handleResize);
		visualViewportTarget?.addEventListener("scroll", handleResize);
		window.addEventListener("resize", handleResize);
	});

	onUnmounted(() => {
		isMounted = false;
		visualViewportTarget?.removeEventListener("resize", handleResize);
		visualViewportTarget?.removeEventListener("scroll", handleResize);
		visualViewportTarget = null;
		window.removeEventListener("resize", handleResize);
	});

	return { menuProps };
}
